import React, { useMemo, useState } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import {
	DataGridPro,
	GridRowParams,
	GridSortModel,
	GridCallbackDetails,
	useGridApiRef,
} from '@mui/x-data-grid-pro';
import { InventoryItem, InventoryFields, InventoryStatus } from 'common/modules/inventory';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useSelector } from 'react-redux';
import { notUndefined } from 'common/utils/common';

import TableToolbar from '../../components/TableToolbar';
import CustomNoRowsOverlay from '../../components/CustomNoRowsOverlay';

import CustomColumnMenu from './CustomColumnMenu';
import CustomColumnsPanel from '../../components/CustomColumnsPanel';
import { useTranslation } from 'services/localization';
import { RiLayoutColumnLine, RiFilter3Fill, RiDownloadLine } from 'react-icons/ri';
import { moveElementLeft, moveElementRight } from 'common/utils/arrays';
import { api } from 'common/frontend/api';
import { getFilteredRowsBySearchValue } from '../../utils';
import { getInventoryRowItems } from './rows';
import { getFixedColumns, getDynamicColumns } from './columns';
import { getDataGridLocales } from 'services/localization/getDataGridLocales';
import { push, Routes } from 'routing';
import { isEmpty } from 'lodash';

interface Props {
	inventoryFields: InventoryFields;
	inventoryItems: InventoryItem[];
	selectedRowIds: string[];
	setSelectedRowIds: (ids: string[]) => void;
	searchValue?: string;
	statuses?: InventoryStatus[];
	selectedLocationIds?: string[];
	visibleSkuIds?: string[];
	openRenameColumnDialog: (arg: string) => void;
	openDeleteColumnDialog: (arg: string) => void;
}

const ArticlesTable = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const apiRef = useGridApiRef();
	const allLocations = useSelector(ShopSelectors.activeShopAllLocations);
	const useDinVariation = useSelector(ShopSelectors.useDinVariation);
	const shopDinCollectionMethod = useSelector(ShopSelectors.shopDinCollectionMethod);
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const [pageSize, setPageSize] = useState<number>(50);
	const [sortModel, setSortModel] = useState<GridSortModel>([
		{
			field: 'article_code',
			sort: 'asc',
		},
	]);
	const {
		inventoryItems,
		visibleSkuIds,
		inventoryFields,
		searchValue = '',
		statuses,
		selectedLocationIds,
	} = props;
	const hasFilters = !!searchValue || !isEmpty(statuses) || !isEmpty(visibleSkuIds);

	const inventoryRowItems = useMemo(
		() =>
			getInventoryRowItems({
				skuIds: visibleSkuIds,
				statuses,
				inventoryItems,
				selectedLocationIds,
				storeLocations: allLocations,
				t,
			}),
		[allLocations, inventoryItems, selectedLocationIds, visibleSkuIds, statuses, t],
	);

	const hasMultipleLocations = allLocations.length > 1;

	const moveColumn = async (id: string, direction: 1 | -1) => {
		const fieldIdx = inventoryFields.order.findIndex((fieldId) => fieldId === id);
		if (fieldIdx > -1) {
			const updatedFields =
				direction === 1
					? moveElementRight(inventoryFields.order, fieldIdx)
					: moveElementLeft(inventoryFields.order, fieldIdx);

			await api().inventoryConfigurations.doc(shopId).update({
				'fields.order': updatedFields,
			});
		}
	};

	const fixedColumns = useMemo(
		() =>
			getFixedColumns({
				t,
				multiLocation: hasMultipleLocations,
				showDinVariation: useDinVariation,
				showSoleLength: !!shopDinCollectionMethod,
			}),
		[hasMultipleLocations, shopDinCollectionMethod, t, useDinVariation],
	);
	const dynamicColumns = useMemo(() => getDynamicColumns(inventoryFields), [inventoryFields]);

	const tableColumns = useMemo(() => [...fixedColumns, ...dynamicColumns].filter(notUndefined), [
		dynamicColumns,
		fixedColumns,
	]);

	const filteredRows = useMemo(() => getFilteredRowsBySearchValue(inventoryRowItems, searchValue), [
		inventoryRowItems,
		searchValue,
	]);

	const handleRowClick = (
		params: GridRowParams,
		event: React.MouseEvent,
		details: GridCallbackDetails,
	) => {
		if (!!props.selectedRowIds.length) {
			const isRowCurrentlySelected = apiRef.current.isRowSelected(params.id);
			apiRef.current.selectRow(params.id, !isRowCurrentlySelected);
		} else {
			push(Routes.InventoryItem(params.id.toString()), {
				from: `${window.location.pathname}${window.location.search}`,
			});
		}
	};

	return (
		<div className={classes.paper}>
			<div className={classes.flexGrow}>
				<DataGridPro
					autoHeight
					apiRef={apiRef}
					className={classes.datagridRoot}
					density="standard"
					rows={filteredRows}
					columns={tableColumns}
					localeText={getDataGridLocales(t)}
					disableColumnReorder={true}
					checkboxSelection={true}
					pagination
					pageSize={pageSize}
					onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
					rowsPerPageOptions={[25, 50, 100]}
					sortModel={sortModel}
					disableSelectionOnClick={true}
					onRowClick={handleRowClick}
					onSortModelChange={(model) => setSortModel(model)}
					onSelectionModelChange={(newSelectionModel) => {
						props.setSelectedRowIds(newSelectionModel.map((id) => id.toString()));
					}}
					selectionModel={props.selectedRowIds}
					components={{
						Toolbar: TableToolbar,
						ColumnSelectorIcon: RiLayoutColumnLine,
						OpenFilterButtonIcon: RiFilter3Fill,
						ExportIcon: RiDownloadLine,
						ColumnMenu: CustomColumnMenu,
						NoRowsOverlay: CustomNoRowsOverlay,
						ColumnsPanel: CustomColumnsPanel,
					}}
					componentsProps={{
						columnMenu: {
							dynamicColumns: dynamicColumns.map((c) => c?.field).filter(notUndefined),
							openRenameColumnDialog: props.openRenameColumnDialog,
							openDeleteColumnDialog: props.openDeleteColumnDialog,
							moveColumn: moveColumn,
						},
						toolbar: {
							showImport: true,
						},
						noRowsOverlay: {
							title: !!searchValue
								? t('inventory.articles.table.noSearchResults', {
										searchValue,
										defaultValue: 'No matching articles found for "{{searchValue}}"',
								  })
								: !!hasFilters
								? t('inventory.articles.table.noMatchingArticles', 'No matching articles found')
								: t('inventory.articles.table.emptyTitle', 'You have no inventory articles'),
							body: !!hasFilters
								? ''
								: t(
										'inventory.articles.table.emptyDescription',
										`Once you've added articles, you'll be able to manage them here.`,
								  ),
						},
						columnsPanel: {
							dataGridName: 'inventory_articles',
						},
					}}
				/>
			</div>
		</div>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		paper: {
			display: 'flex',
			flexDirection: 'column',
			flex: 1,
			backgroundColor: theme.palette.background.paper,
			border: `1px solid ${theme.palette.border.paper}`,
			borderRadius: 4,
		},
		flexGrow: {
			display: 'flex',
			flexGrow: 1,
		},
		datagridRoot: {
			backgroundColor: theme.palette.background.paper,
			'& .MuiDataGrid-columnHeaders': {
				backgroundColor: theme.palette.background.secondary,
			},
			'& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
				outline: 'none',
			},
			'& .MuiDataGrid-menuIcon': {
				visibility: 'visible',
				width: 'auto',
			},
			'& .MuiDataGrid-toolbar': {
				padding: theme.spacing(1),
			},
			'& .MuiDataGrid-columnSeparator': {
				color: theme.palette.border.table,
			},
			'& .MuiDataGrid-cell': {
				border: 'none',
			},
			'& .MuiDataGrid-row': {
				cursor: 'pointer',
				borderBottom: `1px solid ${theme.palette.border.table}`,
				'& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
					outline: 'none',
				},
			},
			'& .overflow-cell': {
				overflowX: 'auto',
			},
			'& .no-sku-cell': {
				color: theme.palette.error.main,
				fontStyle: 'italic',
			},
			'& .MuiDataGrid-columnHeaders + div': {
				//Forces scrollbar to table bottom
				minHeight: '100%',
			},
			'& .MuiDataGrid-virtualScroller': {
				//Forces scrollbar to table bottom
				minHeight: 'calc(100% - 56px) !important',
			},
		},
	}),
);

export default ArticlesTable;
